import type { ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useT } from "../i18n";

/**
 * Confirmation before an action that cannot be taken back — anything that
 * posts to the ledger or marks money as moved. The ledger is append-only, so
 * a mis-click is only correctable with a reversing entry; one extra step here
 * is the cheaper path.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  note,
  confirmLabel,
  tone = "primary",
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  /** Secondary line, e.g. why the action is irreversible. */
  note?: string;
  confirmLabel?: string;
  tone?: "primary" | "error";
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{body}</Typography>
        {note && (
          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            {note}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {/* Focus starts on the safe choice, so a stray Enter cancels rather than posts. */}
        <Button onClick={onClose} disabled={busy} autoFocus>
          {t("common_cancel")}
        </Button>
        <Button variant="contained" color={tone} onClick={onConfirm} disabled={busy}>
          {confirmLabel ?? t("common_confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
